import ShortId from "short-unique-id";

import options from "@/options";
import Firebase from "@/services/Firebase";

function lastValue(nextValue, changePerc) {
  return nextValue / (1 + changePerc / 100.0);
}
function findAsset(assets, findParams) {
  const index = assets.findIndex((asset) => {
    let isFound = true;
    if (findParams.exchId !== undefined) {
      isFound =
        isFound &&
        (asset.exchId === findParams.exchId ||
          (!findParams.exchId && !asset.exchId));
    }
    if (asset.currencyId && findParams.currencyId) {
      isFound = isFound && asset.currencyId === findParams.currencyId;
    } else {
      isFound =
        isFound &&
        (asset.currency === findParams.currency ||
          asset.currency === findParams.symbol);
    }
    return isFound;
  });
  if (findParams.returnIndex) {
    return index;
  }
  return index > -1 ? assets[index] : null;
}
function prepareAssets(assets, findCoin = null) {
  const findCoinIsFn = typeof findCoin === "function";
  return assets.map((asset) => {
    let currencyId = asset.id || asset.currencyId || null;
    if (
      findCoinIsFn &&
      (!currencyId || currencyId[0] == currencyId[0].toUpperCase())
    ) {
      const coinData = findCoin(asset);
      currencyId = coinData ? coinData.id : currencyId;
    }
    return {
      exchId: asset.exchId || 0,
      currencyId,
      currency: asset.symbol || asset.currency || null,
      holdings: asset.holdings || 0,
      color: asset.color || null,
      notes: asset.notes || null,
    };
  })
    .filter((asset) => asset.exchId === 0) // NOTE: not pass other exchanges except Cointrackr (will remove them from Firebase)
  ;
}

export default {
  namespaced: true,
  state: {
    assets: options.defaultAssets,
    assetsLocal: [],
  },
  getters: {
    changeFromHourAgo: (_state, getters) =>
      getters.currentEvaluation - getters.totalHourAgo,
    changeFrom24HoursAgo: (_state, getters) =>
      getters.currentEvaluation - getters.total24HoursAgo,
    changeFrom7DaysAgo: (_state, getters) =>
      getters.currentEvaluation - getters.total7DaysAgo,

    changePercFromHourAgo: (_state, getters) =>
      getters.totalHourAgo
        ? (getters.currentEvaluation / getters.totalHourAgo - 1) * 100
        : 0,
    changePercFrom24HoursAgo: (_state, getters) =>
      getters.total24HoursAgo
        ? (getters.currentEvaluation / getters.total24HoursAgo - 1) * 100
        : 0,
    changePercFrom7DaysAgo: (_state, getters) =>
      getters.total7DaysAgo
        ? (getters.currentEvaluation / getters.total7DaysAgo - 1) * 100
        : 0,

    totalHourAgo: (_state, getters) =>
      getters.portfolio.reduce(
        (acc, next) => acc + lastValue(next.value, next.percChangeHour),
        0
      ),
    total24HoursAgo: (_state, getters) =>
      getters.portfolio.reduce(
        (acc, next) => acc + lastValue(next.value, next.percChangeDay),
        0
      ),
    total7DaysAgo: (_state, getters) =>
      getters.portfolio.reduce(
        (acc, next) => acc + lastValue(next.value, next.percChangeWeek),
        0
      ),

    portfolioAssetsSortedByName: (_state, getters) =>
      getters.portfolio.slice().sort((a, b) => a.name.localeCompare(b.name)),
    portfolioAssetsSortedByShare: (_state, getters) =>
      getters.portfolio.slice().sort((a, b) => b.share - a.share),

    // add distribution shares, used by chart?
    portfolio: (_state, getters) =>
      getters.assetsWithoutShare.map((asset) =>
        Object.assign(asset, {
          share:
            getters.currentEvaluation !== 0
              ? (100 / getters.currentEvaluation) * asset.value
              : 0,
        })
      ),

    currentEvaluationInBTC: (_state, getters, _rootState, rootGetters) => {
      return (
        getters.currentEvaluationInUSD / rootGetters["coinmarketcap/priceOfBTC"]
      );
    },
    currentEvaluation: (_state, getters) =>
      getters.assetsWithoutShare.reduce((a, b) => a + b.value, 0),
    currentEvaluationInUSD: (_state, getters) =>
      getters.assetsWithoutShareInUSD.reduce((a, b) => a + b.value, 0),

    currentEvaluationPerExchange: (_state, getters) => (exchId) => {
      return getters.assetsWithoutShare
        .filter((v) => v.exchId === exchId)
        .reduce((a, b) => a + b.value, 0);
    },

    assets: (state, _getters, _rootState, rootGetters) =>
      prepareAssets(
        state.assets,
        rootGetters["coinmarketcap/coinByFindParams"]
      ),
    assetsCointrackr: (_state, getters) =>
      getters.assets.filter((v) => v.exchId === 0),
    assetsExchange: (_state, getters) =>
      getters.assets.filter((v) => v.exchId !== 0),

    assetsWithoutShare(_state, getters, _rootState, rootGetters) {
      const convertToCurrency = rootGetters["currency/convertToCurrency"];
      return getters.assetsWithoutShareInUSD.map((asset) => {
        return Object.assign({}, asset, {
          price: convertToCurrency(asset.price || 0),
          value: convertToCurrency(asset.value || 0),
        });
      });
    },
    assetsWithoutShareInUSD(_state, getters, _rootState, rootGetters) {
      const coinsFetchedFromCoinMarketCap =
        rootGetters["coinmarketcap/isReady"];

      return !coinsFetchedFromCoinMarketCap
        ? []
        : getters.assets.map((asset) => {
            const coinData = rootGetters["coinmarketcap/coinByFindParams"](
              asset
            );

            if (coinData) {
              const price = coinData["price_usd"];
              const value = asset.holdings * price;
              const percChangeHour = coinData.percent_change_1h || 0;
              const percChangeDay = coinData.percent_change_24h || 0;
              const percChangeWeek = coinData.percent_change_7d || 0;
              return Object.assign({}, coinData, asset, {
                uniqueId: asset.exchId + asset.currencyId + asset.currency,
                price,
                value,
                percChangeHour,
                percChangeDay,
                percChangeWeek,
              });
            } else {
              const name =
                "" + (asset.id || asset.currencyId || asset.currency);
              const capitalizedName =
                name.charAt(0).toUpperCase() + name.slice(1);
              return Object.assign({}, asset, {
                uniqueId: asset.exchId + asset.currencyId + asset.currency,
                id: asset.currencyId,
                name: capitalizedName,
                name_deleted: `(Deleted) ${capitalizedName}`,
                symbol: asset.currency,
                price: 0,
                value: 0,
                percChangeHour: 0,
                percChangeDay: 0,
                percChangeWeek: 0,
                isDeleted: true,
              });
            }
          });
    },
    assetByFindParams: (_state, getters) => (findParams) => {
      return findAsset(getters.assetsWithoutShareInUSD, findParams);
    },
    assetSimpleFind: (_state, getters) => (findParams) => {
      return findAsset(getters.assets, findParams);
    },

    availableExchanges: (_state, getters) => {
      const exchIds = new Set();
      getters.asset.forEach((v) => exchIds.add(v.exchId));
      return options.exchangesList.filter((v) => exchIds.has(v.exchId));
    },
  },
  mutations: {
    setAssetsMutation(state, assets) {
      state.assets = assets;
    },
    setAssetsLocalMutation(state, assetsLocal) {
      state.assetsLocal = assetsLocal;
    },
  },
  actions: {
    setAssets: (
      { state, commit, dispatch, rootGetters },
      { assets, source }
    ) => {
      // remembering assetsLocal if it's first set from firebase
      if (
        !state.assetsLocal.length &&
        rootGetters["auth/isLoggedIn"] &&
        source === "firebase"
      ) {
        dispatch("setAssetsLocal", state.assets.filter((v) => !v.exchId));
      }

      // TODO: state.assets or [] (empty array?)
      // null can come only from Firebase startFirebaseSync once after register
      const assetsNew = prepareAssets(assets === null ? [] : assets);

      // TODO: how to handle empty?
      if (!assetsNew.length) {
        return;
      }

      if (rootGetters["auth/isLoggedIn"] && source !== "firebase") {
        Firebase.saveAssets(assetsNew);
      } else {
        commit("setAssetsMutation", assetsNew);

        dispatch(
          "coinmarketcap/refetchCoinsIfHasChanges",
          { assetsNew },
          { root: true }
        );
      }
    },
    setAssetsLocal: ({ commit }, assetsLocal) => {
      commit("setAssetsLocalMutation", prepareAssets(assetsLocal));
    },
    addAsset: ({ state, dispatch }, asset) => {
      const assets = [...state.assets].concat([asset]);
      dispatch("setAssets", { assets });
    },
    removeAsset: ({ state, getters, dispatch }, asset) => {
      const index = findAsset(getters.assets, {
        ...asset,
        returnIndex: true,
      });
      if (index > -1 && state.assets.length > 1) {
        const assets = [...state.assets];
        assets.splice(index, 1);
        dispatch("setAssets", { assets });
      }
    },
    updateAsset: ({ state, getters, dispatch }, assetData) => {
      const index = findAsset(getters.assets, {
        ...assetData,
        returnIndex: true,
      });
      if (index > -1) {
        const assets = [...state.assets];
        assets.splice(index, 1, {
          ...assets[index],
          ...assetData,
        });
        dispatch("setAssets", { assets });
      } else {
        dispatch("addAsset", assetData);
      }
    },
    setDefaultAssets: ({ state, dispatch }) => {
      const assets = state.assetsLocal.length
        ? state.assetsLocal
        : options.defaultAssets;
      dispatch("setAssets", { assets });
      dispatch("setAssetsLocal", []);
    },
    updateExchangeAssets: ({ getters, dispatch }, assets) => {
      const assetsExchange = getters.assetsExchange;
      const assetsExchangeNew = [];
      assets.forEach((v) => {
        const found = findAsset(assetsExchange, v);
        if (found !== null) {
          assetsExchangeNew.push({
            ...found,
            ...v,
          });
        } else {
          assetsExchangeNew.push(v);
        }
      });
      if (assetsExchangeNew.length || assetsExchange.length) {
        setTimeout(() => {
          dispatch("setAssets", {
            assets: [...getters.assetsCointrackr, ...assetsExchangeNew],
          });
        }, 1500);
      }
    },
    async share({ getters, rootGetters }, message) {
      const id = new ShortId().randomUUID(13);
      const hash =
        Math.random()
          .toString(36)
          .substring(7) +
        (rootGetters["auth/userId"] || "") +
        Math.random()
          .toString(36)
          .substring(7);
      await Firebase.getShareLink(id, {
        created: Math.round(new Date().getTime() / 1000),
        message,
        hash,
        change: {
          hour: getters.changePercFromHourAgo,
          day: getters.changePercFrom24HoursAgo,
          week: getters.changePercFrom7DaysAgo,
        },
        assets: getters.portfolioAssetsSortedByShare.map((asset) => {
          const { currencyId, name, symbol, color, share } = asset;
          return { currencyId, name, symbol, color, share };
        }),
      });
      return id;
    },
    getSharedData(_context, id) {
      return Firebase.getSharedData(id);
    },
  },
};
